import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Checkbox from "../../Checkbox";
import {
    getNextHighlightedIndexOnArrowKey,
    useDropdownPosition,
    useOutsideClick,
    useScrollHighlightedIntoView,
} from "../../../../hooks/useDropdownBehavior";
import {
    StyledFilterChevron,
    StyledFilterItem,
    StyledFilterList,
    StyledFilterStateItem,
    StyledFilterTrigger,
    StyledFilterWrapper,
    StyledOptionCheckbox,
} from "./inputs.styles";

/**
 * DataTable toolbar multi-select filter: a trigger that turns black while any option is selected, and a
 * portalled checkbox list. Styled independently of the library MultiSelectDropdown.
 * @param {Array<{value, label, disabled?, disabledReason?}>} [props.options] Selectable options.
 * @param {Function} [props.renderLabel] Renders an option's label (see `../labels/`); defaults to `option.label`.
 * @param {Array} [props.value] Controlled selected values.
 * @param {Function} [props.onChange] Called with `{ target: { value, name } }`, the full next selection.
 * Also: placeholder, onOpenChange, disabled, disabledReason, loading, loadingMessage, emptyMessage, name, id, ariaLabel, className (self-explanatory).
 */
const DataTableMultiSelectFilter = forwardRef(function DataTableMultiSelectFilter(
    {
        options = [],
        renderLabel = (option) => option.label,
        placeholder = "Filter",
        value = [],
        onChange,
        onOpenChange,
        disabled = false,
        disabledReason,
        loading = false,
        loadingMessage = "Loading options...",
        emptyMessage = "No options available",
        name = "",
        id,
        ariaLabel,
        className,
    },
    ref
) {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const selectedValues = Array.isArray(value) ? value : [];
    const isSelected = (option) => selectedValues.some((item) => String(item) === String(option.value));
    const listboxId = id ? `${id}-listbox` : undefined;

    const setOpen = (nextOpen) => {
        setIsOpen(nextOpen);
        setHighlightedIndex(-1);
        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    };

    const handleSelect = (option) => {
        if (disabled || option.disabled) {
            return;
        }
        const nextValue = isSelected(option)
            ? selectedValues.filter((item) => String(item) !== String(option.value))
            : [...selectedValues, option.value];
        const target = { value: nextValue, name };
        if (onChange) {
            onChange({ target, currentTarget: target });
        }
    };

    const handleKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (isOpen && highlightedIndex >= 0 && options[highlightedIndex]) {
                handleSelect(options[highlightedIndex]);
            } else {
                setOpen(!isOpen);
            }
            return;
        }

        if (event.key === "Escape") {
            setOpen(false);
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!isOpen) {
                setOpen(true);
            }
            setHighlightedIndex((current) => getNextHighlightedIndexOnArrowKey(options, current, event.key));
        }
    };

    useOutsideClick(containerRef, listRef, isOpen, () => setOpen(false));

    const menuPosition = useDropdownPosition(containerRef, isOpen);

    useScrollHighlightedIntoView(listRef, isOpen, highlightedIndex);

    useEffect(() => {
        if (highlightedIndex >= options.length) {
            setHighlightedIndex(-1);
        }
    }, [options, highlightedIndex]);

    return (
        <StyledFilterWrapper ref={containerRef} className={className}>
            <StyledFilterTrigger
                ref={ref}
                type="button"
                id={id}
                $active={selectedValues.length > 0}
                $open={isOpen}
                $disabled={disabled}
                disabled={disabled}
                role="combobox"
                aria-label={ariaLabel || placeholder}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                title={disabled ? disabledReason : undefined}
                onClick={() => !disabled && setOpen(!isOpen)}
                onKeyDown={handleKeyDown}
            >
                <span>{placeholder}</span>
                <StyledFilterChevron $open={isOpen} className="fal fa-chevron-down" aria-hidden="true" />
            </StyledFilterTrigger>

            {isOpen && createPortal(
                <StyledFilterList
                    id={listboxId}
                    ref={listRef}
                    role="listbox"
                    aria-multiselectable="true"
                    $top={menuPosition.top}
                    $left={menuPosition.left}
                    $width={menuPosition.width}
                >
                    {loading ? (
                        <StyledFilterStateItem role="status">{loadingMessage}</StyledFilterStateItem>
                    ) : options.length === 0 ? (
                        <StyledFilterStateItem>{emptyMessage}</StyledFilterStateItem>
                    ) : (
                        options.map((option, index) => {
                            const selected = isSelected(option);
                            return (
                                <StyledFilterItem
                                    key={option.value}
                                    role="option"
                                    aria-selected={selected}
                                    aria-disabled={option.disabled || undefined}
                                    $highlighted={index === highlightedIndex}
                                    $disabled={Boolean(option.disabled)}
                                    title={option.disabled ? option.disabledReason : option.title}
                                    onClick={() => handleSelect(option)}
                                    onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
                                >
                                    <StyledOptionCheckbox aria-hidden="true">
                                        <Checkbox
                                            checked={selected}
                                            disabled={option.disabled}
                                            onChange={() => {}}
                                            tabIndex={-1}
                                            size="sm"
                                        />
                                    </StyledOptionCheckbox>
                                    {renderLabel(option)}
                                </StyledFilterItem>
                            );
                        })
                    )}
                </StyledFilterList>,
                document.body
            )}
        </StyledFilterWrapper>
    );
});

DataTableMultiSelectFilter.displayName = "DataTableMultiSelectFilter";

export default DataTableMultiSelectFilter;
